import React, { useState, useEffect } from 'react';
import { translations } from '../translations';
import apiService from '../services/apiService';

const KetoneTest = ({ patientData, taskId, language = 'en', onComplete, onClose }) => {
  const [selectedValue, setSelectedValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const t = translations[language] || translations.en;

  // Charger les réponses existantes si elles existent
  useEffect(() => {
    const loadExistingResponses = async () => {
      if (!patientData || !patientData.patientId || !taskId) return;
      
      try {
        const response = await apiService.getQuestionnaireResponse(patientData.patientId, taskId);
        if (response.success && response.data) {
          const existingResponses = JSON.parse(response.data.responses);
          if (existingResponses.ketone_value !== undefined) {
            setSelectedValue(existingResponses.ketone_value);
            setIsEditing(true);
          }
        }
      } catch (error) {
        console.error('Erreur chargement réponses existantes:', error);
      }
    };

    loadExistingResponses();
  }, [patientData, taskId]);

  // Valeurs des corps cétoniques avec leurs couleurs
  const ketoneValues = [
    { value: 0, label: 'Neg', color: '#f5e6d3', description: { en: 'Negative', de: 'Negativ' } },
    { value: 5, label: '+/-', color: '#f4d4c4', description: { en: 'Traces', de: 'Spuren' } },
    { value: 15, label: '+', color: '#e8a5a5', description: { en: 'Weak', de: 'Schwach' } },
    { value: 40, label: '++', color: '#c0849e', description: { en: 'Medium', de: 'Mittel' } },
    { value: 80, label: '+++', color: '#8b5a96', description: { en: 'Strong', de: 'Stark' } },
    { value: 160, label: '++++', color: '#4a1c40', description: { en: 'Very Strong', de: 'Sehr Stark' } }
  ];

  const handleSubmit = async () => {
    if (selectedValue === null) return;

    setLoading(true);
    try {
      if (isEditing) {
        // Mode édition : utiliser la route de mise à jour
        await apiService.updateQuestionnaireResponse(
          patientData.patientId,
          taskId,
          'KETONE_TEST',
          JSON.stringify({ ketone_value: selectedValue }),
          selectedValue
        );
      } else {
        // Mode création : utiliser la route de soumission
        const responseData = {
          patientId: patientData.patientId,
          taskId: taskId,
          questionnaireType: 'KETONE_TEST',
          responses: JSON.stringify({ ketone_value: selectedValue }),
          score: selectedValue,
          language: language,
          submissionDate: (() => {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');
            const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
            return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}Z`;
          })()
        };
        await apiService.submitQuestionnaire(responseData);
      }
      
      if (onComplete) {
        onComplete(selectedValue);
      }
    } catch (error) {
      console.error('Erreur soumission test cétonique:', error);
      alert(language === 'de' ? 'Fehler beim Speichern des Ketontests' : 'Error saving ketone test');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ketone-test-container">
      <div className="ketone-test-content">
        <div className="ketone-test-header">
          <h2 className="ketone-test-title">
            {language === 'de' 
              ? `Ketonkörper Test ${isEditing ? '(Bearbeiten)' : ''}` 
              : `Ketone Bodies Test ${isEditing ? '(Edit)' : ''}`
            }
          </h2>
          <button className="ketone-test-close" onClick={onClose}>×</button>
        </div>

        <div className="ketone-test-instructions">
          <div className="reference-image-section">
            <div className="reference-image-container">
              <img 
                src="/images/ketone_bandelette.jpg" 
                alt={language === 'de' ? 'Ketonkörper Farbtabelle' : 'Ketone bodies color chart'}
                className="reference-image"
              />
            </div>
          </div>

          <div className="instruction-section">
            <h3>{language === 'de' ? 'Anleitung' : 'Instructions'}</h3>
            <ul>
              <li>{language === 'de' ? 'Entnehmen Sie eine Teststreifen aus der Verpackung' : 'Take a test strip from the package'}</li>
              <li>{language === 'de' ? 'Halten Sie den Teststreifen in Ihren Urinstrahl oder tauchen Sie ihn in eine Urinprobe' : 'Hold the test strip in your urine stream or dip it in a urine sample'}</li>
              <li>{language === 'de' ? 'Warten Sie genau 15 Sekunden' : 'Wait exactly 15 seconds'}</li>
              <li>{language === 'de' ? 'Vergleichen Sie die Farbe des Teststreifens mit der Farbtabelle' : 'Compare the color of the test strip with the color chart'}</li>
            </ul>
          </div>
        </div>

        <div className="ketone-test-selection">
          <h3>{language === 'de' ? 'Wählen Sie Ihre Messung' : 'Select Your Measurement'}</h3>
          <p className="selection-hint">
            {language === 'de' 
              ? 'Klicken Sie auf die Farbe, die am besten mit Ihrem Teststreifen übereinstimmt'
              : 'Click on the color that best matches your test strip'
            }
          </p>

          <div className="color-selection-grid">
            {ketoneValues.map((ketone) => (
              <div
                key={ketone.value}
                className={`color-option ${selectedValue === ketone.value ? 'selected' : ''}`}
                onClick={() => setSelectedValue(ketone.value)}
              >
                <div 
                  className="color-preview"
                  style={{ backgroundColor: ketone.color }}
                ></div>
                <div className="color-info">
                  <span className="color-label">{ketone.label}</span>
                  <span className="color-value">{ketone.value} mg/dL</span>
                  <span className="color-description">
                    {ketone.description[language]}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="modal-actions">
          <button 
            onClick={handleSubmit}
            className="btn btn-primary"
            disabled={selectedValue === null || loading}
          >
            {loading 
              ? (language === 'de' ? 'Speichern...' : 'Saving...') 
              : (language === 'de' ? 'Speichern' : 'Save')
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default KetoneTest; 